/**
 * First-party usage telemetry (docs/analytics.md, D-094).
 *
 * What it is: a queue of small, typed events — "a story was requested", "the
 * quiz was skipped at question 2" — sent as one batch to OUR OWN function
 * (/.netlify/functions/event) every few seconds. The client still only ever
 * talks to our origin (invariant 4); the CSP's connect-src 'self' would block
 * anything else, and there is nothing else.
 *
 * What it is not: it never sends a name, the user-agent string, story text, or
 * anything a child typed except two normalised fields (a search that found
 * nothing, and an animal request), both lowercase letters only, 40 chars max.
 * The `device` id is a random string kept in localStorage so Jeff can tell the
 * iPad from the phone; `session` is a random id per page load. Neither is tied
 * to a person.
 *
 * Fire and forget: `track()` never throws, never awaits, never blocks a tap.
 * A batch that fails to send is dropped — no retries, no storms. When the
 * browser is offline the queue is kept (capped) and sent once it is back.
 *
 * Off switches: localStorage `ab.analytics.v1 = "off"` on the device, or
 * ANALYTICS_DISABLED=1 on the server (which then drops every batch). A browser
 * being driven by automation is off on its own — see isAutomated (D-173).
 */

import { normalizeText } from '../../server/analytics_events.js'

const ENDPOINT = '/.netlify/functions/event'
const DEVICE_KEY = 'ab.device.v1'
const OPT_OUT_KEY = 'ab.analytics.v1'
const FLUSH_MS = 5000
const FLUSH_AT = 20
const BATCH_MAX = 25 // the server's per-batch limit
const QUEUE_MAX = 100 // while offline; oldest events are dropped beyond this

const inBrowser = typeof window !== 'undefined' && typeof navigator !== 'undefined'

let queue = []
let timer = null
let installed = false
let lastScreen = null
let memoryDevice = null
const context = { age: null, version: null }

function randomId() {
  try {
    const bytes = new Uint8Array(8)
    crypto.getRandomValues(bytes)
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  } catch {
    return (Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)).replace(/[^a-z0-9]/g, '').slice(0, 16).padEnd(16, '0')
  }
}

const session = randomId()

function deviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY)
    if (!id || !/^[a-z0-9]{8,32}$/.test(id)) {
      id = randomId()
      localStorage.setItem(DEVICE_KEY, id)
    }
    return id
  } catch {
    // Storage blocked: a per-page-load id keeps the batch valid without persisting anything.
    memoryDevice ||= randomId()
    return memoryDevice
  }
}

/**
 * A browser under automation — Playwright, Puppeteer, Selenium, a headless
 * Chromium (D-173). Every QA run, deploy check and e2e pass is one of these,
 * and each starts with an empty localStorage, so each one used to arrive as a
 * brand-new device on a brand-new session. Real use is three kids on three
 * iPads; an automated visit is noise that no later query can separate out.
 *
 * `navigator.webdriver` is the standard flag and covers a driven browser
 * whether it is headless or not; the UA test catches the headless runners that
 * do not set it. The user-agent string is read here and never sent, exactly as
 * in deviceClass().
 */
export function isAutomated() {
  try {
    if (navigator.webdriver === true) return true
    return /HeadlessChrome|Headless|Playwright|Puppeteer|Lighthouse|PhantomJS|Selenium|\bbot\b|crawler|spider/i.test(navigator.userAgent || '')
  } catch {
    return false
  }
}

/** The device-side opt-out. Read on every send so flipping it takes effect at once. */
export function isEnabled() {
  if (!inBrowser) return false
  if (isAutomated()) return false
  try {
    return localStorage.getItem(OPT_OUT_KEY) !== 'off'
  } catch {
    return true
  }
}

/** Set (or clear) the opt-out: setAnalyticsEnabled(false) writes ab.analytics.v1 = "off". */
export function setAnalyticsEnabled(on) {
  try {
    if (on) localStorage.removeItem(OPT_OUT_KEY)
    else localStorage.setItem(OPT_OUT_KEY, 'off')
  } catch {
    /* ignore */
  }
  if (!on) queue = []
}

/**
 * One of ipad | iphone | android | desktop | other, derived here so the
 * user-agent string itself never leaves the device. iPadOS reports itself as a
 * Mac; the touch-point count tells them apart.
 */
export function deviceClass() {
  try {
    const ua = navigator.userAgent || ''
    const touch = (navigator.maxTouchPoints || 0) > 1
    if (/iPad/.test(ua) || (/Macintosh/.test(ua) && touch)) return 'ipad'
    if (/iPhone|iPod/.test(ua)) return 'iphone'
    if (/Android/.test(ua)) return 'android'
    if (/Windows|Macintosh|Linux|CrOS|X11/.test(ua)) return 'desktop'
    return 'other'
  } catch {
    return 'other'
  }
}

function isStandalone() {
  try {
    return !!(window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true)
  } catch {
    return false
  }
}

/** Fields sent once per batch. */
function common() {
  const c = {
    device: deviceId(),
    session,
    vw: Math.round(window.innerWidth || 0),
    vh: Math.round(window.innerHeight || 0),
    standalone: isStandalone(),
    device_class: deviceClass(),
  }
  if (context.age) c.age = context.age
  if (context.version) c.version = context.version
  return c
}

/** Tell the module the current age band and app version (roster_hash). */
export function setAnalyticsContext({ age, version } = {}) {
  if (age !== undefined) context.age = age ? String(age) : null
  if (version !== undefined) context.version = version ? String(version).toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 16) : null
}

function send(body) {
  let sent = false
  try {
    // A string body goes as text/plain, which needs no preflight anywhere; the
    // server parses JSON regardless of content type.
    if (typeof navigator.sendBeacon === 'function') sent = navigator.sendBeacon(ENDPOINT, body)
  } catch {
    sent = false
  }
  if (!sent) {
    try {
      fetch(ENDPOINT, { method: 'POST', body, keepalive: true, credentials: 'omit', headers: { 'content-type': 'text/plain;charset=UTF-8' } }).catch(() => {})
    } catch {
      /* dropped */
    }
  }
}

/** Send everything queued, now. Safe to call any time. */
export function flush() {
  try {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    if (!queue.length) return
    if (!isEnabled()) {
      queue = []
      return
    }
    if (navigator.onLine === false) return // hold (capped) until the `online` event
    while (queue.length) {
      const events = queue.splice(0, BATCH_MAX)
      send(JSON.stringify({ common: common(), events }))
    }
  } catch {
    queue = []
  }
}

/**
 * Record one event. `props` must be small and typed — the server drops anything
 * not on its allowlist (server/analytics_events.js), so a typo here is silent.
 */
export function track(name, props) {
  try {
    if (!inBrowser || !isEnabled()) return
    if (!installed) install()
    const event = { name, props: props && typeof props === 'object' ? props : {} }
    try {
      event.t = Math.round(performance.now())
    } catch {
      /* optional */
    }
    queue.push(event)
    if (queue.length > QUEUE_MAX) queue.splice(0, queue.length - QUEUE_MAX)
    if (queue.length >= FLUSH_AT) flush()
    else if (!timer) timer = setTimeout(flush, FLUSH_MS)
  } catch {
    /* never let telemetry surface */
  }
}

/** `screen_view`, once per actual change (StrictMode re-runs effects; this dedupes). */
export function trackScreen(screen) {
  if (screen === lastScreen) return
  lastScreen = screen
  track('screen_view', { screen })
}

/** Lowercase letters and spaces, 40 chars — the only free-ish text ever sent. */
export const normalizeQuery = normalizeText

/** Flush on hide/unload, resend once back online, and count uncaught errors (kind only). */
export function install() {
  if (installed || !inBrowser) return
  installed = true
  try {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush()
    })
    window.addEventListener('pagehide', flush)
    window.addEventListener('online', () => {
      if (queue.length) flush()
    })
    window.addEventListener('error', () => track('app_error', { kind: 'exception' }))
    window.addEventListener('unhandledrejection', () => track('app_error', { kind: 'rejection' }))
  } catch {
    /* ignore */
  }
}

// A grown-up can opt a device out with no UI a kid could find: open the app once
// at /?analytics=off (and /?analytics=on to undo). The flag is read on load and
// remembered in localStorage; the address itself is never sent anywhere.
if (inBrowser) {
  try {
    const flag = new URLSearchParams(window.location.search).get('analytics')
    if (flag === 'off') setAnalyticsEnabled(false)
    else if (flag === 'on') setAnalyticsEnabled(true)
  } catch {
    /* ignore */
  }
}

export default { track, trackScreen, flush, install, isEnabled, setAnalyticsEnabled, setAnalyticsContext, deviceClass, normalizeQuery }
